import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { EasyCheckButton, EasyCheckDialog } from '../Controls';
import PropTypes from 'prop-types';

const PrintOptionsDialog = ({
  open,
  onClose,
  printConfig,
  onConfigChange,
  title = "Print Configuration Options"
}) => {
  const handleConfigChange = (field) => (event) => {
    onConfigChange({
      ...printConfig,
      [field]: event.target.checked
    });
  };

  const handleSelectAll = () => {
    onConfigChange({
      includeAcPayee: true,
      includeBeneficiary: true,
      includeDate: true,
      includeAmount: true,
      includeAmountPrefix: true,
      includeAmountSuffix: true,
      includeNonNegotiable: true,
      includeBeneficiary2: true,
      includeRemarks: true,
      chequeSizedPdf: true,
      excludeCurrencyWords: false
    });
  };

  const handleDeselectAll = () => {
    onConfigChange({
      includeAcPayee: false,
      includeBeneficiary: false,
      includeDate: false,
      includeAmount: false,
      includeAmountPrefix: false,
      includeAmountSuffix: false,
      includeNonNegotiable: false,
      includeBeneficiary2: false,
      includeRemarks: false,
      chequeSizedPdf: false,
      excludeCurrencyWords: false
    });
  };

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title={title}
      titleIcon={<SettingsIcon />}
      variant="form"
      maxWidth="sm"
      actions={
        <>
          <EasyCheckButton 
            buttonType="form"
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            onClick={onClose}
          >
            Apply Settings
          </EasyCheckButton>
        </>
      }
    >
      <Box sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure which fields to include when printing cheques. These settings will apply to both individual and bulk printing.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <EasyCheckButton
            buttonType="action"
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
          >
            Select All
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="action"
            variant="outlined"
            size="small"
            onClick={handleDeselectAll}
          >
            Deselect All
          </EasyCheckButton>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeAcPayee}
                onChange={handleConfigChange('includeAcPayee')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Include A/C Payee
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print "A/C Payee Only" crossing on the cheque
                </Typography>
              </Box>
            }
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeBeneficiary}
                onChange={handleConfigChange('includeBeneficiary')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Include Beneficiary Name
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print the beneficiary name on the cheque
                </Typography>
              </Box>
            }
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeDate}
                onChange={handleConfigChange('includeDate')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Include Date
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print the cheque date
                </Typography>
              </Box>
            }
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeAmount}
                onChange={handleConfigChange('includeAmount')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Include Amount
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print the cheque amount (both numerical and in words)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeAmountPrefix}
                onChange={handleConfigChange('includeAmountPrefix')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Amount Prefix **
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print ** before the amount for security
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeAmountSuffix}
                onChange={handleConfigChange('includeAmountSuffix')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Amount Suffix **
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print ** after the amount for security
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.excludeCurrencyWords}
                onChange={handleConfigChange('excludeCurrencyWords')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Exclude Currency Name in Words
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print the amount in words without the currency unit (e.g. "ONE THOUSAND FIVE HUNDRED ONLY")
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeNonNegotiable}
                onChange={handleConfigChange('includeNonNegotiable')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Include Non Negotiable
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print "Non Negotiable" marking on the cheque
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeBeneficiary2}
                onChange={handleConfigChange('includeBeneficiary2')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Beneficiary Line 2
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print the second line of beneficiary name
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.includeRemarks}
                onChange={handleConfigChange('includeRemarks')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Include Remarks
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print remarks/memo on the cheque
                </Typography>
              </Box>
            }
          />
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          PDF Layout
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={printConfig.chequeSizedPdf}
                onChange={handleConfigChange('chequeSizedPdf')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Cheque Sized PDF
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Generate PDF matching exact cheque dimensions (use when printer tray is not centered)
                </Typography>
              </Box>
            }
          />
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
          <Typography variant="body2" color="info.main" fontWeight="medium" gutterBottom>
            Preview Configuration
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {Object.values(printConfig).filter(Boolean).length} of {Object.keys(printConfig).length} options selected
          </Typography>
        </Box>
      </Box>
    </EasyCheckDialog>
  );
};

PrintOptionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  printConfig: PropTypes.shape({
    includeAcPayee: PropTypes.bool,
    includeBeneficiary: PropTypes.bool,
    includeDate: PropTypes.bool,
    includeAmount: PropTypes.bool,
    includeAmountPrefix: PropTypes.bool,
    includeAmountSuffix: PropTypes.bool,
    includeNonNegotiable: PropTypes.bool,
    includeBeneficiary2: PropTypes.bool,
    includeRemarks: PropTypes.bool,
    chequeSizedPdf: PropTypes.bool,
    excludeCurrencyWords: PropTypes.bool,
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default PrintOptionsDialog; 